import { Color, Vector4 } from "three";
import type { Material, MeshStandardMaterial, Texture } from "three";

/**
 * Car paint on baked textures. The ingested models carry their paint inside the albedo texture
 * (one colour per model), so a new colour cannot be a plain material colour: the shader finds the
 * paint's pixels by hue (or, for white and grey cars, by low saturation and high value), keeps their
 * shading (value relative to the paint's own) and swaps the colour. Untextured paint is a colour.
 */

export interface PaintRef {
  /** 0: by hue, 1: low saturation and bright (white/silver paint), 2: dark (black paint). */
  mode: 0 | 1 | 2;
  hue: number;
  /** The paint's typical value (HSV V, sRGB). */
  val: number;
  /** Hue tolerance (0..0.5). */
  tol: number;
}

export function rgbToHsv(r: number, g: number, b: number): [number, number, number] {
  const mx = Math.max(r, g, b);
  const mn = Math.min(r, g, b);
  const d = mx - mn;
  let h = 0;
  if (d > 1e-6) {
    if (mx === r) h = ((g - b) / d) % 6;
    else if (mx === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
    if (h < 0) h += 1;
  }
  return [h, mx > 0 ? d / mx : 0, mx];
}

/** The paint reference from sRGB pixels (RGBA bytes): the strongest saturated hue, else grey. */
export function paintRefFromPixels(data: ArrayLike<number>, fallbackHex = "#ffffff"): PaintRef {
  const bins = new Float64Array(36);
  const vals: number[][] = Array.from({ length: 36 }, () => []);
  const greys: number[] = [];
  const darks: number[] = [];
  let n = 0;
  for (let i = 0; i + 3 < data.length; i += 4) {
    if ((data[i + 3] ?? 255) < 128) continue;
    n++;
    const [h, s, v] = rgbToHsv(data[i]! / 255, data[i + 1]! / 255, data[i + 2]! / 255);
    if (s > 0.25 && v > 0.15) {
      const k = Math.min(35, Math.floor(h * 36));
      bins[k] = bins[k]! + s * v;
      vals[k]!.push(v);
    } else if (s < 0.12 && v > 0.55) greys.push(v);
    else if (s < 0.3 && v < 0.35 && v > 0.015) darks.push(v);
  }
  let best = 0;
  for (let k = 1; k < 36; k++) if (bins[k]! > bins[best]!) best = k;
  const strong = n > 0 && vals[best]!.length / n > 0.04;
  if (strong) {
    // Neighbouring bins count as the same paint (a gradient of shading crosses bin edges).
    const vs = [...vals[(best + 35) % 36]!, ...vals[best]!, ...vals[(best + 1) % 36]!].sort(
      (a, b) => a - b,
    );
    return {
      mode: 0,
      hue: (best + 0.5) / 36,
      val: vs[Math.floor(vs.length * 0.6)] ?? 0.7,
      tol: 0.06,
    };
  }
  if (greys.length > n * 0.05 && greys.length >= darks.length * 0.5) {
    greys.sort((a, b) => a - b);
    return { mode: 1, hue: 0, val: greys[Math.floor(greys.length * 0.6)] ?? 0.85, tol: 0 };
  }
  if (darks.length > n * 0.3) {
    darks.sort((a, b) => a - b);
    return { mode: 2, hue: 0, val: darks[Math.floor(darks.length * 0.7)] ?? 0.12, tol: 0 };
  }
  const hex = /^#[0-9a-f]{6}$/i.test(fallbackHex) ? fallbackHex : "#ffffff";
  const ch = (i: number) => parseInt(hex.slice(1 + i * 2, 3 + i * 2), 16) / 255;
  const [h, , v] = rgbToHsv(ch(0), ch(1), ch(2));
  return { mode: 0, hue: h, val: v || 0.7, tol: 0.06 };
}

/** Pixels of a texture's image, downscaled (browser only; null without a 2D canvas). */
export function texturePixels(tex: Texture, size = 64): Uint8ClampedArray | null {
  const img = tex.image as CanvasImageSource | undefined;
  if (!img || typeof document === "undefined") return null;
  try {
    const c = document.createElement("canvas");
    c.width = size;
    c.height = size;
    const ctx = c.getContext("2d", { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, size, size);
    return ctx.getImageData(0, 0, size, size).data;
  } catch {
    return null;
  }
}

export function paintRefOf(tex: Texture, fallbackHex: string): PaintRef {
  const px = texturePixels(tex);
  return px ? paintRefFromPixels(px, fallbackHex) : paintRefFromPixels([], fallbackHex);
}

/** GLSL: the paint mask and recolour (sRGB-ish HSV over the linear colour). */
export const PAINT_GLSL = /* glsl */ `
uniform vec3 uPaint;
uniform float uPaintOn;
uniform vec4 uPaintRef; // mode, hue, val, tol
vec3 uzHsv(vec3 c) {
  vec4 K = vec4(0.0, -1.0 / 3.0, 2.0 / 3.0, -1.0);
  vec4 p = mix(vec4(c.bg, K.wz), vec4(c.gb, K.xy), step(c.b, c.g));
  vec4 q = mix(vec4(p.xyw, c.r), vec4(c.r, p.yzx), step(p.x, c.r));
  float d = q.x - min(q.w, q.y);
  return vec3(abs(q.z + (q.w - q.y) / (6.0 * d + 1e-6)), d / (q.x + 1e-6), q.x);
}
float uzPaintMask(vec3 lin) {
  vec3 hsv = uzHsv(pow(max(lin, 0.0), vec3(1.0 / 2.2)));
  if (uPaintRef.x < 0.5) {
    float dh = abs(fract(hsv.x - uPaintRef.y + 0.5) - 0.5);
    return (1.0 - smoothstep(uPaintRef.w, uPaintRef.w * 1.9, dh)) * smoothstep(0.12, 0.3, hsv.y)
      * smoothstep(0.05, 0.14, hsv.z);
  }
  if (uPaintRef.x < 1.5) return (1.0 - smoothstep(0.08, 0.16, hsv.y)) * smoothstep(0.42, 0.6, hsv.z);
  return (1.0 - smoothstep(0.22, 0.34, hsv.y)) * (1.0 - smoothstep(0.3, 0.42, hsv.z)) * smoothstep(0.01, 0.03, hsv.z);
}
vec3 uzRepaint(vec3 lin) {
  vec3 hsv = uzHsv(pow(max(lin, 0.0), vec3(1.0 / 2.2)));
  float k = clamp(hsv.z / max(uPaintRef.z, 0.02), 0.0, 1.5);
  vec3 painted = uPaint * pow(k, 2.2);
  return mix(lin, painted, uzPaintMask(lin) * uPaintOn);
}
`;

const cache = new Map<string, Material>();

/**
 * The material of a paint bucket in `hex`: the base itself for the model's own colour on a textured
 * paint, a recolouring clone otherwise. Shared per (base, colour); never dispose from a consumer.
 */
export function paintMaterial(
  base: Material,
  ref: PaintRef | null,
  hex: string,
  isDefault: boolean,
): Material {
  const std = base as MeshStandardMaterial;
  if (ref && isDefault) return base;
  const key = `${base.uuid}|${hex}`;
  const m = cache.get(key);
  if (m) return m;
  const c = std.clone();
  if (!ref || !std.map) {
    c.color.set(hex);
  } else {
    const uPaint = { value: new Color(hex) };
    const uRef = { value: new Vector4(ref.mode, ref.hue, ref.val, ref.tol) };
    c.customProgramCacheKey = () => "uz-paint";
    c.onBeforeCompile = (shader) => {
      shader.uniforms["uPaint"] = uPaint;
      shader.uniforms["uPaintOn"] = { value: 1 };
      shader.uniforms["uPaintRef"] = uRef;
      shader.fragmentShader = shader.fragmentShader
        .replace("#include <common>", `#include <common>\n${PAINT_GLSL}`)
        .replace(
          "#include <map_fragment>",
          "#include <map_fragment>\n  diffuseColor.rgb = uzRepaint( diffuseColor.rgb );",
        );
    };
  }
  cache.set(key, c);
  return c;
}

export type RimStyle = "stock" | "black" | "chrome" | "gold";
export const RIM_STYLES: readonly RimStyle[] = ["stock", "black", "chrome", "gold"];
const RIM: Record<Exclude<RimStyle, "stock">, { color: string; metal: number; rough: number }> = {
  black: { color: "#16181c", metal: 0.6, rough: 0.35 },
  chrome: { color: "#e9edf2", metal: 1, rough: 0.06 },
  gold: { color: "#c9a14a", metal: 1, rough: 0.2 },
};
const rimCache = new Map<string, Material>();

/**
 * A wheel bucket's material with another rim finish: the bright, unsaturated pixels (the rim;
 * the tyre is dark) take the finish's colour, and the material its metal and gloss.
 */
export function rimMaterial(base: Material, style: RimStyle): Material {
  if (style === "stock") return base;
  const key = `${base.uuid}|${style}`;
  const hit = rimCache.get(key);
  if (hit) return hit;
  const std = base as MeshStandardMaterial;
  const c = std.clone();
  const r = RIM[style];
  c.metalness = Math.max(c.metalness, r.metal * 0.7);
  c.roughness = Math.min(c.roughness, r.rough + 0.15);
  const uRim = { value: new Color(r.color) };
  c.customProgramCacheKey = () => "uz-rim";
  c.onBeforeCompile = (shader) => {
    shader.uniforms["uRim"] = uRim;
    shader.fragmentShader = shader.fragmentShader
      .replace(
        "#include <common>",
        `#include <common>
        uniform vec3 uRim;
        float uzLum(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }`,
      )
      .replace(
        "#include <map_fragment>",
        `#include <map_fragment>
        {
          vec3 s = pow(max(diffuseColor.rgb, 0.0), vec3(1.0 / 2.2));
          float mx = max(s.r, max(s.g, s.b));
          float sat = mx > 0.0 ? (mx - min(s.r, min(s.g, s.b))) / mx : 0.0;
          float m = smoothstep(0.28, 0.5, mx) * (1.0 - smoothstep(0.2, 0.4, sat));
          vec3 rim = uRim * (0.55 + 0.6 * uzLum(diffuseColor.rgb));
          diffuseColor.rgb = mix(diffuseColor.rgb, rim, m);
        }`,
      );
  };
  rimCache.set(key, c);
  return c;
}
